import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
} from '@datachain/ui-sdk/common';
import { OrderedMap } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import { DcDxGrids } from '../core/dx-types';
import { DxListsStorage } from '../core/services/dx-lists.storage';
import { ProjectStorage } from '../core/services/project.storage';
import { IMapResponse } from '../requesters/map/map.model';
import { MapItemEntity } from './components/map-list/map-item.entity';
import {
	IMapListExtra,
	MapListEntity,
} from './components/map-list/map-list.entity';

@Injectable()
export class MapAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly dxListsStorage: DxListsStorage,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath,
		private readonly userSettings: ProjectStorage
	) {}

	public generateMapList(response: ReadonlyArray<IMapResponse>): MapListEntity {
		const columns = this.getMapColumns();
		const columnsTuples = response
			.reduce<Array<MapItemEntity>>((acc, item) => {
				const temp = this.generateMapItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});

		const orderedMap = OrderedMap<string, MapItemEntity>(
			columnsTuples.map((item) => [`${item.id}`, item])
		);
		return columns.setElements(orderedMap);
	}

	private getMapColumns(): MapListEntity {
		let availableCols: Array<IMapListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
				width: '5.5rem',
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@map.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 1,
				width: '5.5rem',
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@general.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
				width: '20%',
			},
			{
				field: 'tags',
				alias: 'Tags',
				type: $localize`:i18n=@@map.list.column.tags:`,
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@map.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 5,
				width: '10rem',
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@map.list.column.creationDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 6,
				width: '10rem',
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@map.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 7,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@map.list.column.updateDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 8,
				width: '10rem',
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@map.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 9,
			},
		];
		availableCols = this.dxListsStorage.synchronize<
			IMapListExtra,
			MapItemEntity
		>(DcDxGrids.MapListStandalone, availableCols);
		return MapListEntity.build(availableCols);
	}

	private generateMapItemEntity(response: IMapResponse): MapItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return MapItemEntity.build({
			id: response.id,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
		});
	}
}
